import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  PrimaryButton,
  SecondaryButton,
  EmptyList,
  FilterBar,
  DonorList,
} from './UI';
import { Donor } from '@/types';
import { apiService } from '@/services/api';

interface DonorSearchStepProps {
  onDonorSelected: (donor: Donor) => void;
  onDonorDeselected: () => void;
  onNewDonor: () => void;
  onCancel: () => void;
  selectedDonor?: Donor;
  showNavigationButtons?: boolean;
}

export const DonorSearchStep: React.FC<DonorSearchStepProps> = ({
  onDonorSelected,
  onDonorDeselected,
  onNewDonor,
  onCancel,
  selectedDonor,
  showNavigationButtons = true,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [donors, setDonors] = useState<Donor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setDonors([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Usa la strategia ibrida: server quando online, cache quando offline
      const searchResults = await apiService.searchDonors(searchQuery, 20);
      setDonors(searchResults);
      
      // Mostra info sulla fonte dei dati
      if (isOnline) {
        console.log('🌐 Search results from server');
      } else {
        console.log('📱 Search results from cache');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore durante la ricerca';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDonorSelect = (donor: Donor) => {
    onDonorSelected(donor);
  };

  const handleDonorDeselect = () => {
    onDonorDeselected();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setDonors([]);
    setError(null);
    setHasSearched(false);
  };

  return (
    <Box>
      {/* <Typography variant="h6" gutterBottom>
        Ricerca Donatore
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Cerca un donatore esistente per nome, cognome o email, oppure creane uno nuovo.
      </Typography> */}

      {/* Donatore selezionato */}
      {selectedDonor && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Donatore Selezionato
          </Typography>
          <Box sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'primary.main', 
            borderRadius: 1,
            bgcolor: 'primary.50',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="h6" color="primary.main">
                {selectedDonor.firstName} {selectedDonor.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedDonor.email}
              </Typography>
              {selectedDonor.phone && (
                <Typography variant="body2" color="text.secondary">
                  {selectedDonor.phone}
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleDonorDeselect}
            >
              Cambia
            </Button>
          </Box>
        </Box>
      )}

      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Modalità offline: stai cercando nella cache locale
        </Alert>
      )}

      <FilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
              onClick={handleSearch}
              disabled={isLoading || !searchQuery.trim()}
              size="small"
            >
              {isLoading ? 'Cercando...' : 'Cerca'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              disabled={isLoading}
              size="small"
            >
              Pulisci
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {hasSearched && donors.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Risultati della ricerca ({donors.length})
          </Typography>
          <DonorList
            donors={donors}
            onSelect={handleDonorSelect}
            onDeselect={handleDonorDeselect}
            variant="default"
            showActions={true}
            layout="horizontal"
          />
        </Box>
      )}

      {hasSearched && searchQuery && donors.length === 0 && !isLoading && (
        <EmptyList
          title="Nessun donatore trovato"
          description={`Non sono stati trovati donatori per "${searchQuery}"`}
          icon={<PersonIcon sx={{ fontSize: 48 }} />}
          action={
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              sx={{ mt: 2 }}
            >
              Pulisci Ricerca
            </Button>
          }
        />
      )}


      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom>
          Non hai trovato il donatore che stai cercando?
        </Typography>
        <PrimaryButton
          startIcon={<AddIcon />}
          onClick={onNewDonor}
          sx={{ mt: 1 }}
        >
          Crea Nuovo Donatore
        </PrimaryButton>
      </Box>

      {showNavigationButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <SecondaryButton onClick={onCancel}>
            Annulla
          </SecondaryButton>
        </Box>
      )}
    </Box>
  );
};
